// 별별책방 - 본사 - 원별 사용량 메인 페이지
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from "react";
import ReactEcharts from "echarts-for-react";

import { InputGroup } from "react-bootstrap";

import axios from "@app/lib/axios";

import AgGridReactEx from "@app/components/AgGridReactEx";

import CardStatContent from "../components/CardStatContent";

const statSummaryBase = {
    total_nursery: 0,
    total_class: 0,
    total_k_play_count: 0,
    total_k_play_count_sum: 0,
    total_m_play_count: 0,
    total_m_play_count_sum: 0,
    avg_k_play_count: 0,
    avg_k_play_count_sum: 0,
    avg_m_play_count: 0,
    avg_m_play_count_sum: 0,
};
const defaultColDef = {
    headerClass: "bg-dark text-light",
    cellClass: "text-left",
    filter: false,
    resizable: true,
    sortable: true,
    minWidth: 95,
    width: 95,
};
const Nursery = forwardRef(
    (
        { branchList, statPeriod, selectedCategorySubOption = "k", updateCategorySubOption, selectedCategorySubOption2 = "-", updateCategorySubOption2, onSelectNursery, stat, updateCategoryStat },
        ref
    ) => {
        const [subOptionApplied, setSubOptionApplied] = useState(false);
        const [statSummary, setStatSummary] = useState({ ...statSummaryBase });
        const [tableData, setTableData] = useState(false);
        const [searchParam, setSearchParam] = useState({ value: "" });
        const tableRef = useRef();

        const type = selectedCategorySubOption;
        const typeTitle = type == "k" ? "한글" : "수학";
        const listColumn = [
            {
                headerName: "계약 지사",
                field: "branchName",
                minWidth: 100,
                width: 120,
                pinned: "left",
            },
            {
                headerName: "원명",
                field: "nurseryUniqName",
                minWidth: 125,
                width: 130,
                pinned: "left",
            },
            {
                headerName: "계약 클래스 수",
                field: "contractClass",
                cellClass: "text-center",
                minWidth: 80,
                width: 90,
            },
            {
                headerName: "영상 순 실행 수",
                cellClass: "text-center",
                field: `${type}_v_play_count`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "리뷰 순 실행 수",
                cellClass: "text-center",
                field: `${type}_r_play_count`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "스페셜 순 실행 수",
                cellClass: "text-center",
                field: `${type}_s_play_count`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "영상 누적 실행 수",
                cellClass: "text-center",
                field: `${type}_v_play_count_sum`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "리뷰 누적 실행 수",
                cellClass: "text-center",
                field: `${type}_r_play_count_sum`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "스페셜 누적 실행 수",
                cellClass: "text-center",
                field: `${type}_s_play_count_sum`,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            },
            {
                headerName: "순 실행 수",
                cellClass: "text-center",
                field: `total_${type}_play_count`,
                hide: true,
            },
            {
                headerName: "누적 실행 수",
                cellClass: "text-center",
                field: `total_${type}_play_count_sum`,
                hide: true,
            },
        ];

        useImperativeHandle(ref, () => ({
            getExcelData(callback) {
                const type = selectedCategorySubOption;
                const data = [];
                if (tableData) {
                    tableData.forEach((s) => {
                        data.push([
                            s.branchName,
                            s.nurseryUniqName,
                            s.contractClass,
                            s[`${type}_v_play_count`],
                            s[`${type}_v_play_count_sum`],
                            s[`${type}_r_play_count`],
                            s[`${type}_r_play_count_sum`],
                            s[`${type}_s_play_count`],
                            s[`${type}_s_play_count_sum`],
                            s[`total_${type}_play_count`],
                            s[`total_${type}_play_count_sum`],
                        ]);
                    });
                }
                data.sort((a, b) => b[10] - a[10]);
                const excelData = {
                    fileName: `모모한글수-원별통계-${typeTitle}`,
                    sheets: [
                        {
                            name: "원",
                            title: `원별 콘텐츠 사용 통계 - ${typeTitle}`,
                            columnTitle: {
                                rows: [
                                    { title: ["지사", "원", "계약 클래스 수", "영상", "영상", "리뷰", "리뷰", "스페셜", "스페셜", "합계(순)", "합계(누적)"], originColumn: 0 },
                                    { title: ["순", "누적", "순", "누적", "순", "누적"], originColumn: 3 },
                                ],
                                merges: [
                                    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // branchName
                                    { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // nurseryName
                                    { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // 계약클래스 수
                                    { s: { r: 0, c: 3 }, e: { r: 0, c: 4 } }, // 영상
                                    { s: { r: 0, c: 5 }, e: { r: 0, c: 6 } }, // 리뷰
                                    { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } }, // 스페셜
                                    { s: { r: 0, c: 9 }, e: { r: 1, c: 9 } }, // 합계(순)
                                    { s: { r: 0, c: 10 }, e: { r: 1, c: 10 } }, // 합계(누적)
                                ],
                            },
                            data: data,
                        },
                    ],
                };
                callback(null, excelData);
            },
        }));
        const getStat = () => {
            if (stat) {
                applyData(searchParam, stat);
                return;
            }
            updateCategoryStat(false);
            setStatSummary({ ...statSummaryBase });
            setTableData(false);
            axios({
                method: "POST",
                url: `/api/stat_momo/hq/nurseries`,
                data: {
                    startDate: statPeriod.startDate,
                    endDate: statPeriod.endDate,
                    periodType: statPeriod.type,
                },
            })
                .then((result) => {
                    if (result.data.code === 200) {
                        const nurseries = result.data.nurseries;

                        if (nurseries && nurseries.length > 0) {
                            nurseries.forEach((n) => {
                                n.k_v_play_count = n.k_v_play_count ? n.k_v_play_count : 0;
                                n.k_r_play_count = n.k_r_play_count ? n.k_r_play_count : 0;
                                n.k_s_play_count = n.k_s_play_count ? n.k_s_play_count : 0;
                                n.k_v_play_count_sum = n.k_v_play_count_sum ? n.k_v_play_count_sum : 0;
                                n.k_r_play_count_sum = n.k_r_play_count_sum ? n.k_r_play_count_sum : 0;
                                n.k_s_play_count_sum = n.k_s_play_count_sum ? n.k_s_play_count_sum : 0;

                                n.m_v_play_count = n.m_v_play_count ? n.m_v_play_count : 0;
                                n.m_r_play_count = n.m_r_play_count ? n.m_r_play_count : 0;
                                n.m_s_play_count = n.m_s_play_count ? n.m_s_play_count : 0;
                                n.m_v_play_count_sum = n.m_v_play_count_sum ? n.m_v_play_count_sum : 0;
                                n.m_r_play_count_sum = n.m_r_play_count_sum ? n.m_r_play_count_sum : 0;
                                n.m_s_play_count_sum = n.m_s_play_count_sum ? n.m_s_play_count_sum : 0;

                                n.total_k_play_count = n.k_v_play_count + n.k_r_play_count + n.k_s_play_count;
                                n.total_m_play_count = n.m_v_play_count + n.m_r_play_count + n.m_s_play_count;

                                n.total_k_play_count_sum = n.k_v_play_count_sum + n.k_r_play_count_sum + n.k_s_play_count_sum;
                                n.total_m_play_count_sum = n.m_v_play_count_sum + n.m_r_play_count_sum + n.m_s_play_count_sum;
                                const b = branchList.find((b) => b.id == n.branchId);
                                if (b) {
                                    n.branchType = b.type;
                                }
                            });
                        }

                        nurseries.sort((a, b) => {
                            if (a.branchType != b.branchType) {
                                return a.branchType - b.branchType;
                            } else {
                                return b.contractClass - a.contractClass;
                            }
                        });
                        updateCategoryStat(nurseries);
                    } else {
                        alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                    }
                })
                .catch((error) => {
                    console.log(error);
                    console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
                });
        };
        useEffect(() => {
            if (!subOptionApplied && updateCategorySubOption && updateCategorySubOption2) {
                updateCategorySubOption(
                    [
                        { name: "한글", id: "k" },
                        { name: "수학", id: "m" },
                    ],
                    "k"
                );
                updateCategorySubOption2([{ id: "-", name: "전체" }].concat(branchList), selectedCategorySubOption2 || "-");
                setSubOptionApplied(true);
            }
            return () => {};
        }, []);
        useEffect(() => {
            return () => {};
        }, [subOptionApplied, tableData]);
        useEffect(() => {
            if (subOptionApplied) {
                getStat();
            }
        }, [statPeriod, subOptionApplied]);
        useEffect(() => {
            applySummary(stat);
            applyData(searchParam, stat);
            return () => {};
        }, [stat, searchParam, selectedCategorySubOption, selectedCategorySubOption2]);

        useEffect(() => {
            if (tableRef && tableRef.current) {
                tableRef.current.sizeColumnsToFit();
            }
            return () => {};
        }, [tableData]);

        const handleKeyPress = (e) => {
            if (e.key === "Enter") {
                onSearch();
            }
        };
        const onSearch = () => {
            applyData(searchParam, stat);
        };

        const applySummary = (stat) => {
            const statSummaryNew = { ...statSummaryBase };

            if (stat && stat.length > 0) {
                stat.forEach((n) => {
                    if (selectedCategorySubOption2 == "-" || selectedCategorySubOption2 == n.branchId) {
                        statSummaryNew.total_nursery++;
                        statSummaryNew.total_class += n.contractClass ? n.contractClass : 0;

                        statSummaryNew.total_k_play_count += n.total_k_play_count;
                        statSummaryNew.total_m_play_count += n.total_m_play_count;

                        statSummaryNew.total_k_play_count_sum += n.total_k_play_count_sum;
                        statSummaryNew.total_m_play_count_sum += n.total_m_play_count_sum;
                    }
                });
            }

            statSummaryNew.avg_k_play_count = (statSummaryNew.total_k_play_count / statSummaryNew.total_nursery).toFixed(0);
            statSummaryNew.avg_k_play_count_sum = (statSummaryNew.total_k_play_count_sum / statSummaryNew.total_nursery).toFixed(0);
            statSummaryNew.avg_m_play_count = (statSummaryNew.total_m_play_count / statSummaryNew.total_nursery).toFixed(0);
            statSummaryNew.avg_m_play_count_sum = (statSummaryNew.total_m_play_count_sum / statSummaryNew.total_nursery).toFixed(0);
            setStatSummary(statSummaryNew);
        };

        const applyData = (searchParam, list) => {
            var tableDataNew = listSearch(searchParam, list);
            setTableData(tableDataNew);
        };

        const searchField = ["branchName", "nurseryUniqName"];
        const listSearch = (searchParam, list) => {
            var tableDataNew = [];
            if (list && list.length > 0) {
                if (!searchParam.value) {
                    list.forEach((item) => {
                        tableDataNew.push(item);
                    });
                } else {
                    list.forEach((item) => {
                        let inserted = false;
                        searchField.forEach((field) => {
                            if (!inserted && item[field] && item[field].indexOf(searchParam.value) >= 0) {
                                tableDataNew.push(item);
                                inserted = true;
                            }
                        });
                    });
                }
            }
            if (selectedCategorySubOption2 != "-") {
                tableDataNew = tableDataNew.filter((t) => t.branchId == selectedCategorySubOption2);
            }
            return tableDataNew;
        };

        const onGridReady = (params) => {};

        const onRowDataChanged = (params) => {
            params.api.sizeColumnsToFit();
        };

        const onRowSelected = (e) => {
            if (onSelectNursery && e && e.data) {
                onSelectNursery(e.data.nurseryUniqId, e.data.nurseryUniqName, e.data.branchId, e.data.branchName);
            }
        };

        return (
            <div className="stat-branch-content">
                <div className="row mb-2">
                    <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6">
                        <CardStatContent id="1" title={`계약 원 수`} titleTotal="합계:" total={statSummary.total_nursery} titleBgClass="bg-tomonote" />
                    </div>
                    <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6">
                        <CardStatContent id="1" title={`계약 클래스 수`} titleTotal="합계:" total={statSummary.total_class} titleBgClass="bg-tomonote" />
                    </div>
                    <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6">
                        <CardStatContent
                            id="1"
                            title={`전체 콘텐츠 순 실행 수`}
                            average={statSummary[`avg_${type}_play_count`]}
                            titleTotal="합계:"
                            total={statSummary[`total_${type}_play_count`]}
                            titleBgClass="bg-tomonote"
                        />
                    </div>
                    <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6">
                        <CardStatContent
                            id="1"
                            title={`전체 콘텐츠 누적 실행 수`}
                            average={statSummary[`avg_${type}_play_count_sum`]}
                            titleTotal="합계:"
                            total={statSummary[`total_${type}_play_count_sum`]}
                            titleBgClass="bg-tomonote"
                        />
                    </div>
                </div>
                <div className="row mb-1">
                    <div className="col-xl-8 col-lg-7 col-md-6 col-sm-12 col-12 pl-4 d-flex">
                        <h5 className="mb-0" style={{ alignSelf: "end" }}>
                            <span>
                            {tableData ? tableData.length : "0"} / {statSummary.total_nursery}
                            </span>
                        </h5>
                    </div>
                    <div className="col-xl-4 col-lg-5 col-md-6 col-sm-12 col-12">
                        <InputGroup className="input-group my-group">
                            <input
                                className="form-control"
                                type="text"
                                value={searchParam ? searchParam.value : ""}
                                placeholder="지사명|원명"
                                onChange={(e) => {
                                    const param = { ...searchParam };
                                    param.value = e.target.value;
                                    setSearchParam(param);
                                }}
                                onKeyPress={handleKeyPress}
                            />
                            <InputGroup.Append>
                                <input type="submit" className="btn btn-primary" value="검색" onClick={onSearch} />
                            </InputGroup.Append>
                        </InputGroup>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
                        <AgGridReactEx
                            ref={tableRef}
                            height="550"
                            defaultColDef={defaultColDef}
                            columnDefs={listColumn}
                            headerHeight={40}
                            rowData={tableData}
                            rowSelection="single"
                            onGridReady={onGridReady}
                            onRowDataChanged={onRowDataChanged}
                            onRowClicked={(e) => {
                                onRowSelected(e);
                            }}
                            pagination={true}
                            overlayNoRowsTemplate={`<span>통계 자료가 없습니다.</span>`}
                        />
                    </div>
                </div>
            </div>
        );
    }
);

export default Nursery;
